import GLightbox from 'glightbox';
import {getCookie, hasAccessProtectionCookie, hasMediaCookie, hasStatisticCookie, setCookie} from '../Utility/CookieUtility';
import {injectTagManager} from '../Utility/GoogleTagManagerUtility';
import {buildAccessProtectionForm} from '../Functions/AccessProtection';
import {getCookieCountryCode} from '../CountryRestriction/CountryRestrictionUtility';
import {removeHash} from '../Utility/HashUtility';

export class CookieBar {

  private youTubeConsentState: number = -1;

  constructor() {
    this.init();
  }

  init() {
    if (isUserFromChina()) {
      window.addEventListener('hashchange', () => {
        this.hashChangeListener();
      });
      const settingsButton = document.querySelector('.js-cookieOverlaySettings');
      settingsButton?.addEventListener('click', () => {
        this.showOverlay();
      });

      const currentCookie = getCookie('acceptCookies');
      if (currentCookie !== 'necessary' && currentCookie !== 'media' && currentCookie !== 'statistic' && currentCookie !== 'all') {
        this.showOverlay();
      } else if (hasStatisticCookie()) {
        this.activateTracking();
      }

      this.initAcceptButtons();
    } else {
      if (!document.querySelector('.js-no-tagmanager')) {
        injectTagManager();
      }
      const cookieCountryCode = getCookieCountryCode();
      const firstScriptTag: any = document.getElementsByTagName('script')[0];
      const usercentricsScriptTag = document.createElement('script');
      usercentricsScriptTag.async = true;
      usercentricsScriptTag.id = 'usercentrics-cmp';
      usercentricsScriptTag.type = 'application/javascript';
      usercentricsScriptTag.src = 'https://app.usercentrics.eu/browser-ui/latest/loader.js';
      usercentricsScriptTag.dataset.language = cookieCountryCode === 'ro' || cookieCountryCode === 'hu' ? cookieCountryCode : document.documentElement.lang;
      usercentricsScriptTag.dataset.settingsId = 'qSDkKAE8A';
      if (location.hostname.match('wwwserver.net') || location.hostname.match('iveins.de')) {
        usercentricsScriptTag.dataset.version = 'preview';
      }

      firstScriptTag.parentNode.insertBefore(usercentricsScriptTag, firstScriptTag);

      waitForUsercentrics(() => {
        this.appendYouTubeApi(false);
        window.addEventListener('UC_UI_VIEW_CHANGED', (e) => {
          this.appendYouTubeApi(true);
        });
      });

      waitForUsercentrics(() => {
        // use this script tag to ensure typoscript conditions for page layouts
        let userlikeScriptTag: HTMLScriptElement | null = document.querySelector('#userlike') as HTMLScriptElement;
        if (!userlikeScriptTag) {
          return;
        }

        setTimeout(
          () => {
            const userlikeConsent = (window as any).UC_UI.getServicesBaseInfo().find(data => (data.id === '3WonF-t8zb3T9D')).consent.status; //Userlike ID
            if (userlikeScriptTag && userlikeConsent) {
              userlikeScriptTag.type = 'text/javascript';
              userlikeScriptTag.src = 'https://userlike-cdn-widgets.s3-eu-west-1.amazonaws.com/3ca28a6317694cb49ce27f5c7fe34a372e6ba092fb184e6e836e0a74c077e9a2.js';
            }
          },
          7000);
      });
    }

    this.initVideos();

    if (isUserFromChina()) {
      const acceptSelectionButton = document.querySelector('.js-acceptSelection');
      if (!acceptSelectionButton) {
        return;
      }
      acceptSelectionButton.addEventListener('click', () => {
        const statisticCookie = document.querySelector('.js-statisticCookie') as HTMLInputElement;
        const mediaCookie = document.querySelector('.js-mediaCookie') as HTMLInputElement;
        if (statisticCookie && statisticCookie.checked && mediaCookie && mediaCookie.checked) {
          this.acceptCookies('all');
        } else if (statisticCookie && statisticCookie.checked) {
          this.acceptCookies('statistic');
        } else if (mediaCookie && mediaCookie.checked) {
          this.acceptCookies('media');
        } else {
          this.acceptCookies('necessary');
        }
        this.closeOverlay();
      });
    }
  }

  initAcceptButtons() {
    const acceptAllButton = document.querySelector('.js-acceptAll');
    if (acceptAllButton) {
      acceptAllButton.addEventListener('click', () => {
        this.acceptCookies('all');
        this.closeOverlay();
      });
    }

    const acceptNecessaryButton = document.querySelector('.js-acceptNecessary');
    if (acceptNecessaryButton) {
      acceptNecessaryButton.addEventListener('click', () => {
        this.acceptCookies('necessary');
        this.closeOverlay();
      });
    }

    const showSettingsButton = document.querySelector('.js-showCookieSettings');
    if (showSettingsButton) {
      showSettingsButton.addEventListener('click', () => {
        this.showSettings();
      });
    }
  }

  initVideos() {
    const videoLightboxes = document.querySelectorAll('.js-videoLightbox');
    videoLightboxes.forEach((videoLightbox) => {
      videoLightbox.addEventListener('click', (e) => {
        if (!(videoLightbox instanceof HTMLElement)) {
          return;
        }

        const videoId = videoLightbox.dataset.id;
        if (!videoId) {
          return;
        }
        if (isUserFromChina()) {
          if (videoLightbox.dataset.protected === '1') {
            if (hasAccessProtectionCookie()) {
              if (hasMediaCookie()) {
                this.buildVideoLightbox(parseInt(videoId, 10));
              } else {
                this.buildCookieLightbox(videoLightbox);
              }
            } else {
              buildAccessProtectionForm(videoLightbox);
            }
          } else {
            if (hasMediaCookie()) {
              this.buildVideoLightbox(parseInt(videoId, 10));
            } else {
              this.buildCookieLightbox(videoLightbox);
            }
            return false;
          }
        } else {
          waitForUsercentrics(
            () => {
              const youtubeConsent = (window as any).UC_UI.getServicesBaseInfo().find(data => (data.id === 'BJz7qNsdj-7')).consent.status; //YouTube Video ID

              if (videoLightbox.dataset.protected === '1') {
                if (hasAccessProtectionCookie()) {
                  if (youtubeConsent) {
                    this.buildVideoLightbox(parseInt(videoId, 10));
                  } else {
                    this.buildCookieLightbox(videoLightbox);
                  }
                } else {
                  buildAccessProtectionForm(videoLightbox);
                }
              } else {
                if (youtubeConsent) {
                  this.buildVideoLightbox(parseInt(videoId, 10));
                } else {
                  this.buildCookieLightbox(videoLightbox);
                }
                return false;
              }
            });
        }
      });
    });
  }

  appendYouTubeApi(reload: boolean) {
    const services = (window as any).UC_UI.getServicesBaseInfo();
    const youtubeConsent: boolean = services.find(data => (data.id === 'BJz7qNsdj-7')).consent.status;

    if (youtubeConsent) {
      this.youTubeConsentState = 1;
      if (document.getElementById('youtube-iframe-api')) {
        return;
      }
      if (null === document.querySelector('figure.video')) {
        return;
      }
      const firstScriptTag: any = document.getElementsByTagName('script')[0];
      const youtubeIframeApi = document.createElement('script');
      youtubeIframeApi.async = true;
      youtubeIframeApi.id = 'youtube-iframe-api';
      youtubeIframeApi.type = 'application/javascript';
      youtubeIframeApi.src = 'https://www.youtube.com/iframe_api';
      firstScriptTag.parentNode.insertBefore(youtubeIframeApi, firstScriptTag);
      return;
    }
    if (reload) {
      // only on toggle
      if (this.youTubeConsentState === 1) {
        this.youTubeConsentState = 0;
        window.location.reload();
      }
    }
  }

  buildCookieLightbox(videoLightboxElement) {
    if (!(videoLightboxElement instanceof HTMLElement)) {
      return;
    }
    const cookieOverlayVideos = document.querySelector('.js-cookieOverlayVideos');
    if (!(cookieOverlayVideos instanceof HTMLElement)) {
      return;
    }

    const cookieOverlayLightbox = new GLightbox({
      skin: 'clean glightbox--cookieOverlay',
      width: 'inital',
      height: 'inital',
      touchNavigation: false,
      draggable: false,
      elements: [
        {
          content: cookieOverlayVideos.innerHTML,
        },
      ],
    });
    cookieOverlayLightbox.open();
    this.initVideoCookieOverlayWatcher(videoLightboxElement, cookieOverlayLightbox);
  }

  buildVideoLightbox(videoId: number, cookieOverlayLightbox?) {
    const videoParams = document.querySelector('.js-accessProtectionData[data-id="' + videoId + '"]');
    if (!(videoParams instanceof HTMLElement)) {
      return;
    }

    if (videoParams.dataset.id) {
      window.location.hash = `video=${videoParams.dataset.id}`;
    }

    const videoLightbox = new GLightbox({
      skin: 'clean glightbox--video',
      touchNavigation: false,
      draggable: false,
      moreLength: 350,
      moreText: videoParams.dataset.moreText,
      width: 'inital',
      height: 'inital',
      elements: [
        {
          content: videoParams.dataset.source,
          description: videoParams.dataset.description || '',
        },
      ],
    });

    videoLightbox.on('close', () => {
      if (window.location.hash.includes('video=')) {
        removeHash();
      }
    });

    videoLightbox.open();
  }

  initVideoCookieOverlayWatcher(videoLightboxElement, cookieOverlayLightbox) {
    const acceptButton = document.querySelector('.glightbox-container .js-acceptVideos');
    if (!(acceptButton instanceof HTMLElement)) {
      return;
    }
    acceptButton.addEventListener('click', (e) => {
      if (isUserFromChina()) {
        const cookie = getCookie('acceptCookies');
        if (cookie === 'all') {
          return;
        }
        if (cookie === 'statistic') {
          this.acceptCookies('all');
        } else {
          this.acceptCookies('media');
        }
      } else {
        waitForUsercentrics(
          () => {
            (window as any).UC_UI.acceptService('BJz7qNsdj-7'); //YouTube Video ID
            this.appendYouTubeApi(false);
          });
      }
      cookieOverlayLightbox.close();
      cookieOverlayLightbox.destroy();

      // wait currently open glightbox to close to avoid weird visuals
      window.setTimeout(
        () => {
          const videoId = videoLightboxElement.dataset.id;
          if (!videoId) {
            return;
          }
          this.buildVideoLightbox(parseInt(videoId, 10), cookieOverlayLightbox);
        },
        600);
    });
  }

  showSettings() {
    const cookieOverlay = document.querySelector('.js-cookieOverlay');
    if (cookieOverlay) {
      cookieOverlay.classList.add('cookie--showSettings');
    }
  }

  closeOverlay() {
    document.body.classList.remove('body--noscroll');
    const cookieOverlay = document.querySelector('.js-cookieOverlay');
    if (cookieOverlay) {
      cookieOverlay.classList.remove('cookie--show');
    }
    location.reload();
  }

  showOverlay() {
    if (window.location.hash === '#noCookieMessage') {
      const url = location.href.replace(/#.*/, '');
      history.pushState({}, '', url);
    } else {
      document.body.classList.add('body--noscroll');
      const cookieOverlay = document.querySelector('.js-cookieOverlay');
      const statisticInput = document.querySelector('.js-statisticCookie') as HTMLInputElement;
      const mediaInput = document.querySelector('.js-mediaCookie') as HTMLInputElement;
      if (cookieOverlay) {
        if (statisticInput && hasStatisticCookie()) {
          statisticInput.checked = true;
        }
        if (mediaInput && hasMediaCookie()) {
          mediaInput.checked = true;
        }
        cookieOverlay.classList.add('cookie--show');
      }
    }
  }

  acceptCookies(status) {
    let domain = window.location.hostname;
    if (domain !== 'faulhaber.com.cn') {
      domain = domain.split(/\./).slice(1).join('.');
    }
    if (status === 'all' || status === 'statistic') {
      this.activateTracking();
      setCookie('acceptCookies', status, 365, domain);
    } else {
      this.deactivateTracking();
      setCookie('acceptCookies', status, 7, domain);
    }
  }

  hashChangeListener() {
    const urlParts = document.URL.split('#');
    const anchor = (urlParts.length > 1) ? urlParts[1] : null;
    if (anchor === 'cookies') {
      this.showOverlay();
      const url = location.href.replace(/#.*/, '');
      history.pushState({}, '', url);
    }
  }

  activateTracking() {
    if ('_etracker' in window && typeof window._etracker.enableCookies !== 'undefined') {
      window._etracker.enableCookies(document.domain);
    }
  }

  deactivateTracking() {
    if ('_etracker' in window && typeof window._etracker.disableCookies !== 'undefined') {
      window._etracker.disableCookies(document.domain);
    }
  }
}

export function waitForUsercentrics(callback: () => any) {
  let counter = 50;
  const checkForUsercentrics = () => {
    if (typeof (window as any).UC_UI === 'object' && typeof (window as any).UC_UI.getServicesBaseInfo() === 'object') {
      callback();
      return;
    }
    if (counter > 0) {
      counter--;
      window.setTimeout(checkForUsercentrics, 100);
    }
  };
  checkForUsercentrics();
}

export function isUserFromChina(): boolean {
  return !!document.querySelector('.js-cookieOverlay');
}
